import React, { useMemo, useState } from "react";
import { Layout, Form, Table, Button, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Topbar from "../topbar/Index";
import { getOrgDetails, getOrgSettings, getProfile, getSelectedFcYearDetails, updateUserSettings } from "../../thunks/user-management";
import SideNavMenu, { getMuenuItems } from "../side-nav-menu/Index";
import UserIcon from "../../assets/user icon.png";
import { addAppTab, capitalizeFirstLetter, decrementPendingRequests, incrementPendingRequests, notifySuccess, useMouseTrap } from "../../utils";
import { SortableAntTableParent, DragHandle, getSortableRow } from "../utils/sortable-ant-table/Index";
import { CustomInput, CustomModal, CustomSelect, DataTable } from "../utils/custom-elements/Index";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import Marquee from "react-fast-marquee";

export const getDefaultWebHomePageShortcuts = (industry) => {
  switch (industry) {
    case "Textile":
    case "Weaving": {
      return [
        { id: "/transaction/SAL", name: "Sales" },
        { id: "/transaction/PUR", name: "Purchase" },
        { id: "/transaction/RCP", name: "Receipt" },
        { id: "/transaction/PYM", name: "Payment" },
        { id: "/transaction/GIS", name: "Mill Issue" },
        { id: "/transaction/PRO", name: "Mill Receive" },
        { id: "/transaction/JIS", name: "Job Issue" },
        { id: "/transaction/PJW", name: "Job Bill" },
      ]
    }
    case "Agency": {
      return [
        { id: "/transaction/APUR", name: "Agency Purchase" },
        { id: "/transaction/APYM", name: "Agency Payment" },
        { id: "/transaction/RCP", name: "Receipt" },
        { id: "/reports/agency/outstanding", name: "Outstanding Report" },
        { id: "/reports/agency/commission", name: "Commission Report" },
      ]
    }
    case "Jobwork": {
      return [
        { id: "/transaction/JIN", name: "Job Inward" },
        { id: "/transaction/SAL", name: "Sales" },
        { id: "/transaction/PUR", name: "Purchase" },
        { id: "/transaction/RCP", name: "Receipt" },
        { id: "/transaction/PYM", name: "Payment" },
        { id: "/reports/job/job-at-in", name: "Job Report" },
      ]
    }
    default: {
      return [
        { id: "/transaction/SAL", name: "Sales" },
        { id: "/transaction/PUR", name: "Purchase" },
        { id: "/transaction/RCP", name: "Receipt" },
        { id: "/transaction/PYM", name: "Payment" },
      ]
    }
  }
}

const getDefaultAppHomePageShortcuts = (industry, orgSettings) => {
  switch (industry) {
    case "Agency": {
      return [
        { id: "AgencyPurchases", name: "Agency Purchase" },
        { id: "AgencyPayments", name: "Agency Payment" },
        { id: "AgencyPurchaseReturns", name: "Agency Return Goods" },
        { id: "Receipts", name: "Commission Receipt" },
        { id: "AgencyOutstanding", name: "Agency Outstanding" },
        { id: "AgencyCommission", name: "Pending Commission" },
        { id: "Accounts", name: "Accounts" },
      ]
    }
    case "Jobwork": {
      return [
        { id: "Invoices", name: "Invoices" },
        { id: "JobAtWarehouse", name: "Job At Warehouse" },
        orgSettings?.modules?.externalJobwork?.enabled ? { id: "FinishAtJob", name: "Job At Out" } : null,
        { id: "Receivables", name: "Receivables" },
        { id: "Payables", name: "Payables" },
        { id: "Accounts", name: "Accounts" },
      ]
    }
    default: {
      return [
        { id: "Invoices", name: "Invoices" },
        { id: "ItemRateList", name: "Item Rate List" },
        { id: "Receivables", name: "Receivables" },
        { id: "Payables", name: "Payables" },
        { id: "GreyAtMill", name: "Grey At Mill" },
        { id: "FinishAtJob", name: "Finish At Job" },
        { id: "Accounts", name: "Accounts" },
      ]
    }
  }
}

const HomePageShortcutsModal = ({ handleCancel }) => {
  const [form] = Form.useForm();
  const [modalElement, setModalElement] = useState();
  const dispatch = useDispatch();
  const profile = useSelector(state => getProfile(state));
  const orgDetails = useSelector(state => getOrgDetails(state));
  const orgSettings = useSelector(state => getOrgSettings(state));
  const industry = orgDetails.industry;
  const rawSectionName = industry === "Textile" ? "Grey" : industry === "Weaving" ? "Yarn" : "Raw";
  const userSettings = profile?.settings || {};
  const initialValues = {
    webHomePageShortcuts: userSettings.webHomePageShortcuts ?? getDefaultWebHomePageShortcuts(industry),
    appHomePageShortcuts: userSettings.appHomePageShortcuts ?? getDefaultAppHomePageShortcuts(industry, orgSettings)
  }
  const menuItems = useMemo(() => getMuenuItems(industry), [industry]);
  const menuItemsOptions = Object.values(menuItems).map(obj => {
    const group = obj.path.split("/")[1];
    return {
      label: `[${capitalizeFirstLetter(group)}] ${obj.title}`,
      value: obj.path
    }
  });

  const appHomePageShortcutOptions = [
    { value: "Invoices", label: "Invoices" },
    { value: "Purchase", label: "Purchase" },
    { value: "GeneralPurchase", label: "General Purchase" },
    { value: "JobIssue", label: "Job Issue" },
    { value: "JobReceiveChallan", label: "Job Receive Challan" },
    { value: "JobReceiveBill", label: "Job Receive Bill" },
    { value: "StockInward", label: "Stock Inward" },
    { value: "StockOutward", label: "Stock Outward" },
    { value: "StockMovement", label: "Stock Movement" },
    { value: "SalesChallan", label: "Sales Challan" },
    { value: "Purchase Challan", label: "Purchase Challan" },
    { value: "SalesReturn", label: "Sales Return" },
    { value: "PurchaseReturn", label: "Purchase Return" },
    { value: "ItemRateList", label: "Item Rate List" },
    { value: "Accounts", label: "Accounts" },
    { value: "Receivables", label: "Receivables" },
    { value: "Payables", label: "Payables" },
    { value: "GreyAtShop", label: `${rawSectionName} At Shop` },
    { value: "GreyAtMill", label: "Mill Pending" },
    { value: "FinishAtJob", label: "Job Out Pending" },
    { value: "JobAtWarehouse", label: "Job In Pending" },
    { value: "AgencyPurchases", label: "Agency Purchases" },
    { value: "AgencyPayments", label: "Agency Payments" },
    { value: "AgencyPurchaseReturns", label: "Agency Purchase Returns" },
    { value: "AgencyOutstanding", label: "Agency Outstanding" },
    { value: "AgencyCommission", label: "Agency Commission" },
    { value: "Receipts", label: "Receipt Entries" },
    { value: "Payments", label: "Payment Entries" },
    { value: "AgencyPurchaseReport", label: "Agency Purchase Report" },
  ];

  const handleClickSubmit = async () => {
    try {
      incrementPendingRequests();
      const values = await form.validateFields();
      const webHomePageShortcuts = values.webHomePageShortcuts?.filter(obj => obj.id && obj.name);
      const appHomePageShortcuts = values.appHomePageShortcuts?.filter(obj => obj.id && obj.name);
      const request = {};
      if (JSON.stringify(getDefaultWebHomePageShortcuts(industry)) !== JSON.stringify(webHomePageShortcuts)) {
        request.webHomePageShortcuts = webHomePageShortcuts;
      } else {
        request.webHomePageShortcuts = null;
      }
      if (JSON.stringify(getDefaultAppHomePageShortcuts(industry, orgSettings)) !== JSON.stringify(appHomePageShortcuts)) {
        request.appHomePageShortcuts = appHomePageShortcuts;
      } else {
        request.appHomePageShortcuts = null;
      }
      await dispatch(updateUserSettings(request));
      notifySuccess("Updated successfully");
      handleCancel();
    } finally {
      decrementPendingRequests();
    }
  }
  useMouseTrap({ current: modalElement }, {
    "ctrl+s,alt+s,command+s": (e) => {
      e.preventDefault();
      handleClickSubmit();
    }
  }, [modalElement]);

  return (
    <CustomModal
      width={1200}
      onCancel={handleCancel}
      title="Customize Home Page Shortcuts"
      suppressDragging
      contentRef={setModalElement}
    >
      <Form form={form} initialValues={initialValues}>
        <p style={{ fontWeight: "bold", fontSize: 16 }}>Web</p>
        <Form.List name="webHomePageShortcuts">
          {(fields, { add, remove }) => (
            <SortableAntTableParent form={form} formListId="webHomePageShortcuts">
              {(RowContext) => (
                <Table
                  className="striped-table"
                  style={{ marginTop: 16 }}
                  size="small"
                  bordered
                  pagination={false}
                  rowKey="key"
                  dataSource={fields.map((_, i) => ({ key: i + 1 }))}
                  components={{ body: { row: getSortableRow(RowContext) } }}
                  summary={() => (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Button tabIndex={-1} size="small" onClick={() => add({})}>
                            <PlusOutlined />
                          </Button>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                >
                  <Table.Column
                    title="Sort"
                    width="48px"
                    className="drag-visible"
                    render={() => <DragHandle RowContext={RowContext} />}
                  />
                  <Table.Column
                    title="Sr"
                    dataIndex="sr"
                    width={48}
                    align="center"
                    render={(_, record, index) => index + 1}
                  />
                  <Table.Column
                    title="Shortcut"
                    dataIndex="id"
                    render={(_, record, index) => (
                      <Form.Item
                        name={[fields[index].name, "id"]}
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                      >
                        <CustomSelect
                          showSearch
                          options={menuItemsOptions}
                        />
                      </Form.Item>
                    )}
                  />
                  <Table.Column
                    title="Name"
                    dataIndex="name"
                    width={200}
                    render={(_, record, index) => (
                      <Form.Item
                        name={[fields[index].name, "name"]}
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                      >
                        <CustomInput required uppercase={false} />
                      </Form.Item>
                    )}
                  />
                  <Table.Column
                    title={<CloseOutlined />}
                    dataIndex="cancel"
                    width={32}
                    align="center"
                    render={(_, record, index) => (
                      <CloseOutlined onClick={() => remove(fields[index].name)} />
                    )}
                  />
                </Table>
              )}
            </SortableAntTableParent>
          )}
        </Form.List>
        <br />
        <p style={{ fontWeight: "bold", fontSize: 16 }}>Mobile</p>
        <Form.List name="appHomePageShortcuts">
          {(fields, { add, remove }) => (
            <SortableAntTableParent form={form} formListId="appHomePageShortcuts">
              {(RowContext) => (
                <Table
                  className="striped-table"
                  style={{ marginTop: 16 }}
                  size="small"
                  bordered
                  pagination={false}
                  rowKey="key"
                  dataSource={fields.map((_, i) => ({ key: i + 1 }))}
                  components={{ body: { row: getSortableRow(RowContext) } }}
                  summary={() => (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Button tabIndex={-1} size="small" onClick={() => add({})}>
                            <PlusOutlined />
                          </Button>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                >
                  <Table.Column
                    title="Sort"
                    width="48px"
                    className="drag-visible"
                    render={() => <DragHandle RowContext={RowContext} />}
                  />
                  <Table.Column
                    title="Sr"
                    dataIndex="sr"
                    width={48}
                    align="center"
                    render={(_, record, index) => index + 1}
                  />
                  <Table.Column
                    title="Shortcut"
                    dataIndex="id"
                    render={(_, record, index) => (
                      <Form.Item
                        name={[fields[index].name, "id"]}
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                      >
                        <CustomSelect
                          showSearch
                          options={appHomePageShortcutOptions}
                        />
                      </Form.Item>
                    )}
                  />
                  <Table.Column
                    title="Name"
                    dataIndex="name"
                    width={200}
                    render={(_, record, index) => (
                      <Form.Item
                        name={[fields[index].name, "name"]}
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                      >
                        <CustomInput required uppercase={false} />
                      </Form.Item>
                    )}
                  />
                  <Table.Column
                    title={<CloseOutlined />}
                    dataIndex="cancel"
                    width={32}
                    align="center"
                    render={(_, record, index) => (
                      <CloseOutlined onClick={() => remove(fields[index].name)} />
                    )}
                  />
                </Table>
              )}
            </SortableAntTableParent>

          )}
        </Form.List>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Space>
            <Button type="primary" onClick={handleClickSubmit}>Save</Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </Space>
        </div>
      </Form>
    </CustomModal>
  )
}

const KeyboardShorcutsModal = ({ handleCancel }) => {
  return (
    <CustomModal onCancel={handleCancel} width="720px" title="Keyboard Shortcuts">
      <DataTable
        containerStyle={{ width: "100%", height: "100%" }}
        rowHeight={30}
        enableSearch={false}
        data={[
          { desc: "Go to any screen (search)", shortcut: "Alt + G" },
          { desc: "Focus to navigation menu", shortcut: "Alt + N" },
          { desc: "Insert transaction directly from anywhere", shortcut: "Ins" },
          { desc: "Change selected tab", shortcut: "Alt + Left/Right Arrow" },
          { desc: "Change year/company", shortcut: "Alt + F1" },
          { desc: "Export any table to excel", shortcut: "Ctrl + E" },
          {},
          { desc: "Insert remark in transaction", shortcut: "F1" },
          { desc: "Change page filters/options", shortcut: "F2" },
          { desc: "Create new entry from dropdown", shortcut: "F3" },
          { desc: "Edit active item from dropdown", shortcut: "F4" },
          { desc: "Ledger report", shortcut: "F5" },
          { desc: "Orders report", shortcut: "F6" },
          { desc: "Sales receivable", shortcut: "F7" },
          { desc: "Purchase payable", shortcut: "F8" },
          { desc: "Item rate list", shortcut: "F9" },
          { desc: "Late payment interest", shortcut: "F11" },
          { desc: "Calculator", shortcut: "F12" },
          {},
          { desc: "Bank Slip", shortcut: "Ctrl + B" },
          { desc: "Bank Recon", shortcut: "Alt + B" },
          { desc: "Bank Statement", shortcut: "Ctrl + Shift + B" },
          {},
          { desc: "Account group balance", shortcut: "Ctrl + F5" },
          { desc: "Trial balance", shortcut: "Ctrl + F6" },
          { desc: "Trading & PNL", shortcut: "Ctrl + F7" },
          { desc: "Balance sheet", shortcut: "Ctrl + F8" },
          { desc: "Multi JV entry (from account group balance report)", shortcut: "Shift + Insert" },
          {},
          { desc: "Sales list", shortcut: "Ctrl + 1" },
          { desc: "Purchase list", shortcut: "Ctrl + 2" },
          { desc: "Receipt list", shortcut: "Ctrl + 3" },
          { desc: "Payment list", shortcut: "Ctrl + 4" },
          { desc: "Mill Issue list", shortcut: "Ctrl + 5" },
          { desc: "Mill Receive list", shortcut: "Ctrl + 6" },
          { desc: "Job Work Issue list", shortcut: "Ctrl + 7" },
          { desc: "Job Work Receive list", shortcut: "Ctrl + 8" },
          {},
          { desc: "Sales entry", shortcut: "Alt + 1" },
          { desc: "Purchase entry", shortcut: "Alt + 2" },
          { desc: "Receipt entry", shortcut: "Alt + 3" },
          { desc: "Payment entry", shortcut: "Alt + 4" },
          { desc: "Mill Issue entry", shortcut: "Alt + 5" },
          { desc: "Mill Receive entry", shortcut: "Alt + 6" },
          { desc: "Job Work Issue entry", shortcut: "Alt + 7" },
          { desc: "Job Work Receive entry", shortcut: "Alt + 8" },
        ]}
        columns={[
          { id: "desc", isNavigable: true, autoSize: true },
          { id: "shortcut", isNavigable: true, autoSize: true, suppressSizeToFit: true },
        ]}
      />
    </CustomModal>
  );
};

const { Content, Sider } = Layout;
const PageLayout = ({ contentStyle = {}, children }) => {
  const [collapsed, setCollapsed] = useState((localStorage.getItem("sidenavCollapsed") || "false") === "true");
  const fcYearDetails = useSelector((state) => getSelectedFcYearDetails(state));
  const isClosed = fcYearDetails.closed;
  const { fname, lname, settings } = useSelector((state) => getProfile(state));
  const orgDetails = useSelector(state => getOrgDetails(state));
  const industry = orgDetails.industry;
  const webHomePageShortcuts = settings?.webHomePageShortcuts ?? getDefaultWebHomePageShortcuts(industry);
  const [shortcutsModalVisible, setShortcutsModalVisible] = useState(false);
  const [homePageShortcutsModalVisible, setHomePageShortcutsModalVisible] = useState(false);
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const isFreezed = todayDate >= orgDetails.freeze_date;
  useMouseTrap({ current: document.body }, {
    "ctrl+k": (e) => {
      e.preventDefault();
      setShortcutsModalVisible(true);
    },
  }, [])
  return (
    <Layout style={{ height: "100vh" }} >
      <Topbar />
      <Layout hasSider style={{ overflowY: "hidden" }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => {
            setCollapsed(value);
            localStorage.setItem("sidenavCollapsed", value.toString());
          }}
          style={{
            overflow: 'auto',
            height: '100vh',
            backgroundColor: "#19185f"
          }}>
          <div style={{ padding: 16, backgroundColor: "#12114a" }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
              <div style={{
                width: collapsed ? 32 : 64,
                height: collapsed ? 32 : 64,
                background: "white",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s"
              }}>
                <img src={UserIcon} alt="User" style={{ height: collapsed ? 32 : 64, transition: "all 0.2s" }} />
              </div>
            </div>
            {!collapsed && (
              <div style={{ color: "white", padding: "0px 8px", textAlign: "center", marginTop: 8, fontWeight: "bold" }}>{fname} {lname}</div>
            )}
          </div>
          <SideNavMenu />
        </Sider>
        <Layout>
          <div style={{
            display: "flex",
            alignItems: "center",
            padding: "2px 12px",
            color: "black",
            borderBottom: "1px solid #ccc",
            lineHeight: "18px",
            backgroundColor: "#ebf9ff"
          }}>
            <span
              className="material-icons-outlined"
              style={{ fontSize: 16, marginRight: 4, color: "black" }}
              onClick={() => setHomePageShortcutsModalVisible(true)}
            >
              mode_edit
            </span>
            <span style={{ marginRight: 8 }}>
              Shortcuts:
            </span>
            <div className="shortcuts" style={{ flex: 1, overflowX: "auto", display: "flex", alignItems: "center" }}>
              {webHomePageShortcuts?.map(({ id, name }, index) => {
                return (
                  <>
                    {index > 0 && (
                      <span
                        style={{
                          height: 16,
                          width: 1,
                          margin: "0px 8px",
                          display: "inline-block",
                          color: "black"
                        }}
                      >|</span>
                    )}
                    <span style={{ cursor: "pointer", whiteSpace: "nowrap", color: "black" }} onClick={() => addAppTab(id, name)}>{name}</span>
                  </>
                )
              })}
            </div>
            <span
              className="material-icons-outlined"
              style={{ fontSize: 20, color: "black", cursor: "pointer" }}
              onClick={() => setShortcutsModalVisible(true)}
            >
              keyboard
            </span>
          </div>
          <Content
            className={isClosed ? "year-closed" : ""}
            style={{
              flex: 1,
              overflowY: "auto",
              ...contentStyle,
            }}
          >

            {children}
          </Content>
          {todayDate >= orgDetails.payment_alert_date && (
            <Marquee
              direction="left"
              autoFill
              pauseOnHover
              style={{ padding: 4, background: "#fff2f0", color: "#d9363e", textAlign: "center", fontWeight: "bold", fontSize: 16 }}
            >
              Please clear due payment of software
              {isFreezed ? (
                <div><span style={{ padding: "0px 4px" }}>before {format(new Date(orgDetails.freeze_date), "dd MMM yyyy")}</span></div>
              ) : <span style={{ padding: "0px 2px" }}></span>} to continue using software without any interruption |
              सॉफ़्टवेयर का उपयोग बिना किसी रुकावट के जारी रखने के लिए,
              कृपया सॉफ़्टवेयर का बकाया भुगतान
              {isFreezed ? (
                <div><span style={{ padding: "0px 8px" }}>{format(new Date(orgDetails.freeze_date), "dd MMM yyyy")}</span> के पहले</div>
              ) : <span style={{ padding: "0px 2px" }}></span>} कर दीजिय ।
            </Marquee>
          )}
        </Layout>
      </Layout>
      {shortcutsModalVisible && (
        <KeyboardShorcutsModal handleCancel={() => setShortcutsModalVisible(false)} />
      )}
      {homePageShortcutsModalVisible && <HomePageShortcutsModal handleCancel={() => setHomePageShortcutsModalVisible(false)} />}
    </Layout>
  );
};

export default PageLayout;
